import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { CalendarClock, CheckCircle2, Video } from "lucide-react";

/**
 * Remote Consulting MVP — a client requests a consultation and picks a preferred
 * date + time. Reuses the existing Submission model (type='consultation') and
 * Admin can update status via the existing Submissions console.
 *
 * Query params supported for smart pre-selection:
 *   ?service=<key>          — pre-selects the requested HawKam service.
 *   ?assessment=<id>        — links this consultation to an assessment result.
 */
export default function Consultation() {
  const { t, lang } = useI18n();
  const [sp] = useSearchParams();
  const preService = sp.get("service") || "";
  const preAssessment = sp.get("assessment") || "";

  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    service: preService,
    date: "", time: "",
    message: "",
    consent: true,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
  }, []);

  // Minimum allowed date = tomorrow (ISO YYYY-MM-DD)
  const minDate = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  }, []);

  const canSubmit = form.name && form.email && form.date && form.time && form.consent;

  const submit = async () => {
    setBusy(true);
    try {
      const iso = new Date(`${form.date}T${form.time}:00`).toISOString();
      // If linked to an assessment, encode in `service` field so the admin can
      // see the linkage without a schema change.
      const svc = preAssessment
        ? `assessment:${preAssessment}${form.service ? `+${form.service}` : ""}`
        : form.service;
      const { data } = await api.post("/consultation", {
        name: form.name, email: form.email, phone: form.phone || null,
        company: form.company || null, service: svc || null,
        appointment_at: iso,
        message: form.message || null,
        consent: !!form.consent,
      });
      setDone({ id: data.id, appointment_at: data.appointment_at, status: data.status });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="container-x py-24" data-testid="consult-thanks">
        <div className="card-elev p-12 max-w-2xl">
          <CheckCircle2 size={40} className="text-gold" />
          <h1 className="mt-4 text-3xl md:text-4xl text-navy">
            {lang === "ar" ? "تم استلام طلبك" : "Request received"}
          </h1>
          <span className="divider-gold" />
          <p className="text-navy/75 leading-relaxed">
            {lang === "ar"
              ? "استلمنا طلب الاستشارة الخاص بك. سيتواصل فريقنا لتأكيد الموعد قبل التاريخ المطلوب."
              : "We have received your consultation request. Our team will confirm the appointment before the requested date."}
          </p>
          <div className="mt-6 text-sm text-navy/70">
            <div><b>{lang === "ar" ? "المرجع" : "Reference"}:</b> HAWKAM-{done.id.slice(0, 8)}</div>
            {done.appointment_at && (
              <div><b>{lang === "ar" ? "الموعد المطلوب" : "Requested time"}:</b>{" "}
                {new Date(done.appointment_at).toLocaleString(lang === "ar" ? "ar-EG" : "en-GB")}</div>
            )}
            <div><b>{lang === "ar" ? "الحالة" : "Status"}:</b>{" "}
              <span className="chip !bg-amber-50 !text-amber-800 !border-amber-200">Requested</span></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-x py-16" data-testid="consult-page">
      <div className="max-w-3xl">
        <span className="eyebrow">
          {lang === "ar" ? "استشارة عن بُعد" : "Remote Consulting"}
        </span>
        <h1 className="mt-4 text-4xl md:text-5xl text-navy flex items-center gap-3">
          <Video className="text-gold" size={38} />
          {lang === "ar" ? "احجز استشارتك عن بُعد" : "Book your remote consultation"}
        </h1>
        <span className="divider-gold" />
        <p className="text-navy/75 leading-relaxed">
          {lang === "ar"
            ? "املأ النموذج التالي واختر تاريخ ووقت مناسبين — سيتواصل فريقنا لتأكيد الموعد."
            : "Fill in the form and choose a preferred date + time — our team will confirm the appointment."}
        </p>
      </div>

      <div className="mt-10 max-w-3xl card-elev p-8 md:p-10">
        {preAssessment && (
          <div className="mb-6 text-xs text-navy/70 bg-cream/60 border border-navy/10 px-3 py-2">
            {lang === "ar" ? "مرتبطة بنتيجة تقييم: " : "Linked to assessment: "}
            <code className="text-gold">{preAssessment.slice(0, 8)}</code>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <input data-testid="consult-name" className="field-box"
                 placeholder={t("common.name")} value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input data-testid="consult-email" type="email" className="field-box"
                 placeholder={t("common.email")} value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="consult-phone" className="field-box"
                 placeholder={t("common.phone")} value={form.phone}
                 onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <input data-testid="consult-company" className="field-box"
                 placeholder={t("common.company")} value={form.company}
                 onChange={(e) => setForm({ ...form, company: e.target.value })} />
          <select data-testid="consult-service" className="field-box md:col-span-2"
                  value={form.service}
                  onChange={(e) => setForm({ ...form, service: e.target.value })}>
            <option value="">
              {lang === "ar" ? "— اختر الخدمة (اختياري) —" : "— Select a service (optional) —"}
            </option>
            {services.map((s) => (
              <option key={s.id} value={s.key || s.id}>
                {lang === "ar" ? (s.title_ar || s.title_en) : (s.title_en || s.title_ar)}
              </option>
            ))}
          </select>
          <div>
            <label className="text-xs font-semibold text-navy/70 mb-1 flex items-center gap-1">
              <CalendarClock size={12} /> {lang === "ar" ? "التاريخ المطلوب" : "Requested date"}
            </label>
            <input data-testid="consult-date" type="date" className="field-box w-full"
                   min={minDate} value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-navy/70 mb-1 block">
              {lang === "ar" ? "الوقت المطلوب" : "Requested time"}
            </label>
            <input data-testid="consult-time" type="time" className="field-box w-full"
                   value={form.time}
                   onChange={(e) => setForm({ ...form, time: e.target.value })} />
          </div>
          <textarea data-testid="consult-message"
                    className="field-box md:col-span-2 min-h-[100px]"
                    placeholder={lang === "ar" ? "موضوع الاستشارة (اختياري)" : "Consultation topic (optional)"}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <label className="md:col-span-2 flex items-center gap-2 text-navy/80 text-sm">
            <input type="checkbox" className="w-4 h-4 accent-gold"
                   checked={form.consent}
                   onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                   data-testid="consult-consent" />
            {lang === "ar"
              ? "أوافق على معالجة بياناتي وفقاً لسياسة الخصوصية."
              : "I consent to my data being processed per the Privacy Policy."}
          </label>
        </div>

        <div className="mt-8 flex items-center gap-3">
          <button data-testid="consult-submit"
                  onClick={submit} disabled={!canSubmit || busy}
                  className="btn-gold disabled:opacity-40">
            {busy ? "…" : (lang === "ar" ? "إرسال الطلب" : "Request consultation")}
          </button>
          <Link to="/" className="btn-outline-navy">{t("common.back")}</Link>
        </div>
      </div>
    </div>
  );
}
